import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Database,
  Star,
  BookOpen,
  CheckCircle,
  XCircle,
  ArrowRight,
  Loader2,
  Activity,
  Users,
  Zap,
  TrendingUp,
  Clock,
  FileCode,
  Sparkles,
} from 'lucide-react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { isFeatureEnabled } from '@/config/features';

const Home: React.FC = () => {
  const [engineStatus, setEngineStatus] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState({
    totalQueries: 0,
    activeConnections: 0,
    savedQueries: 0,
    successRate: 0,
  });

  useEffect(() => {
    checkEngineStatus();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    // Mock stats - replace with actual API calls
    setStats({
      totalQueries: 1234,
      activeConnections: 8,
      savedQueries: 156,
      successRate: 95.5,
    });
  };

  const checkEngineStatus = async () => {
    setChecking(true);
    try {
      const isHealthy = await api.checkHealth();
      setEngineStatus(isHealthy);
    } catch (error) {
      setEngineStatus(false);
    } finally {
      setChecking(false);
    }
  };

  const statsCards = [
    {
      title: 'Total Queries',
      value: stats.totalQueries.toLocaleString(),
      change: '+12.5%',
      icon: Activity,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Active Connections',
      value: stats.activeConnections,
      change: '+2',
      icon: Database,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Saved Queries',
      value: stats.savedQueries,
      change: '+18',
      icon: Star,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: 'Success Rate',
      value: `${stats.successRate}%`,
      change: '+2.3%',
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  const quickActions = [
    {
      icon: Sparkles,
      title: 'AI Query Builder',
      description: 'Generate SQL with natural language',
      link: '/query',
      gradient: 'from-blue-600 to-cyan-600',
    },
    {
      icon: Database,
      title: 'Connect Database',
      description: 'Add a new database connection',
      link: '/connections',
      gradient: 'from-green-600 to-emerald-600',
    },
    // Conditionally include Golden SQLs based on feature flag
    ...(isFeatureEnabled('GOLDEN_SQL') ? [{
      icon: FileCode,
      title: 'View Queries',
      description: 'Browse your query history',
      link: '/golden-sqls',
      gradient: 'from-purple-600 to-pink-600',
    }] : []),
  ];

  const recentActivity = [
    {
      type: 'query',
      title: 'Customer analytics query executed',
      time: '2 minutes ago',
      status: 'success',
    },
    {
      type: 'connection',
      title: 'Production database connected',
      time: '1 hour ago',
      status: 'success',
    },
    {
      type: 'query',
      title: 'Monthly revenue report generated',
      time: '3 hours ago',
      status: 'success',
    },
    {
      type: 'error',
      title: 'Query failed: Timeout exceeded',
      time: '5 hours ago',
      status: 'error',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b border-[#e6e6ea] px-5 py-3 shrink-0">
        <h1 className="text-[18px] font-semibold text-[#22212d]">Home</h1>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-6">
        {/* Welcome Section */}
        <div className="mb-6">
          <h2 className="text-[24px] font-medium text-[#22212d] mb-1">
            Welcome back!
          </h2>
          <p className="text-[14px] text-[rgba(34,33,45,0.6)]">
            Here's an overview of your database query activity
          </p>
        </div>

        {/* Engine Status Alert */}
        {!checking && (
          <Alert className={`mb-6 ${engineStatus ? 'border-green-500/50 bg-green-50' : 'border-destructive/50 bg-destructive/10'}`}>
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {engineStatus ? (
                    <>
                      <div className="relative">
                        <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                        <div className="absolute inset-0 h-3 w-3 bg-green-500 rounded-full animate-ping" />
                      </div>
                      <span className="text-[13px] font-medium">Engine is running smoothly</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-destructive" />
                      <span className="text-[13px] font-medium">Engine connection failed</span>
                    </>
                  )}
                </div>
                {!engineStatus && (
                  <div className="text-[12px] text-[rgba(34,33,45,0.6)]">
                    Please check backend configuration
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="hover:shadow-md transition-shadow bg-white border-[#e6e6ea]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] text-[rgba(34,33,45,0.6)]">{stat.title}</p>
                      <p className="text-[22px] font-semibold mt-1 text-[#22212d]">{stat.value}</p>
                      <p className="text-[12px] text-green-600 mt-1.5">{stat.change}</p>
                    </div>
                    <div className={`p-2.5 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <Card className="bg-white border-[#e6e6ea]">
              <CardHeader className="pb-4">
                <CardTitle className="text-[16px] font-semibold text-[#22212d]">Quick Actions</CardTitle>
                <CardDescription className="text-[13px] text-[rgba(34,33,45,0.6)]">Start working with your databases</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {quickActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <Link key={index} to={action.link}>
                        <div className="relative group overflow-hidden rounded border border-[#e6e6ea] p-4 hover:shadow-md hover:border-primary/30 transition-all cursor-pointer bg-white">
                          <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />
                          <Icon className="h-7 w-7 mb-2.5 text-primary" />
                          <h3 className="text-[14px] font-semibold mb-1 text-[#22212d]">{action.title}</h3>
                          <p className="text-[12px] text-[rgba(34,33,45,0.6)]">
                            {action.description}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

        {/* Recent Activity */}
        <div>
          <Card className="bg-white border-[#e6e6ea]">
            <CardHeader className="pb-4">
              <CardTitle className="text-[16px] font-semibold text-[#22212d] flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className={`mt-1 h-2 w-2 rounded-full ${
                      activity.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-[13px] font-medium text-[#22212d]">{activity.title}</p>
                      <p className="text-[12px] text-[rgba(34,33,45,0.5)]">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        </div>

        {/* Getting Started Card */}
        <Card className="mt-6 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20 border-[#e6e6ea]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[16px] font-semibold text-[#22212d] flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Getting Started with Dytafly
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 md:grid-cols-${isFeatureEnabled('GOLDEN_SQL') ? '4' : '3'} gap-4`}>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[13px] font-bold text-primary">1</span>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-[#22212d]">Configure Settings</p>
                  <p className="text-[12px] text-[rgba(34,33,45,0.5)] mt-1">
                    Set up API keys and MongoDB
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[13px] font-bold text-primary">2</span>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-[#22212d]">Connect Database</p>
                  <p className="text-[12px] text-[rgba(34,33,45,0.5)] mt-1">
                    Add and sync your databases
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[13px] font-bold text-primary">3</span>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-[#22212d]">Write Queries</p>
                  <p className="text-[12px] text-[rgba(34,33,45,0.5)] mt-1">
                    Use natural language to query
                  </p>
                </div>
              </div>
              {isFeatureEnabled('GOLDEN_SQL') && (
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[13px] font-bold text-primary">4</span>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-[#22212d]">Save Golden SQLs</p>
                    <p className="text-[12px] text-[rgba(34,33,45,0.5)] mt-1">
                      Store successful queries
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Home;